<script setup lang="ts">
import { PageProps } from '@/types'
import BankerAccountController from '@controllers/Banks/BankerAccountController'
import { useForm, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const page = usePage<PageProps>()

const isVisible = ref(false)

const form = useForm({
  first_name: '',
  last_name: '',
  email: '',
  is_admin: false,
})

const canManageRoles = computed(() => page.props.auth.user.user_permissions?.includes('manage client users') ?? false)

const roleOptions = computed(() => [
  { label: t('roles.banker'), value: false },
  { label: t('roles.admin'), value: true },
])

function createUser() {
  form.submit(BankerAccountController.store(), {
    preserveScroll: true,
    onSuccess: () => {
      form.reset()
      isVisible.value = false
    },
  })
}
</script>

<template>
  <Button :label="t('title')" icon="pi pi-user-plus" @click="isVisible = true" />

  <Dialog v-model:visible="isVisible" modal :header="t('title')" class="w-full max-w-lg">
    <div class="mb-4 flex items-center gap-4">
      <label for="first_name" class="min-w-24 font-semibold">{{ t('first_name') }}</label>
      <div class="flex-auto">
        <InputText
          id="first_name"
          v-model="form.first_name"
          class="w-full"
          :invalid="!!form.errors.first_name"
          autofocus />
        <small v-if="form.errors.first_name" class="text-danger">
          {{ form.errors.first_name }}
        </small>
      </div>
    </div>

    <div class="mb-4 flex items-center gap-4">
      <label for="last_name" class="min-w-24 font-semibold">{{ t('last_name') }}</label>
      <div class="flex-auto">
        <InputText id="last_name" v-model="form.last_name" class="w-full" :invalid="!!form.errors.last_name" />
        <small v-if="form.errors.last_name" class="text-danger">
          {{ form.errors.last_name }}
        </small>
      </div>
    </div>

    <div class="mb-4 flex items-center gap-4">
      <label for="email" class="min-w-24 font-semibold">{{ t('email') }}</label>
      <div class="flex-auto">
        <InputText id="email" v-model="form.email" type="email" class="w-full" :invalid="!!form.errors.email" />
        <small v-if="form.errors.email" class="text-danger">
          {{ form.errors.email }}
        </small>
      </div>
    </div>

    <div v-if="canManageRoles" class="mb-4 flex items-center gap-4">
      <span class="min-w-24 font-semibold">{{ t('role') }}</span>
      <div class="flex flex-row items-center gap-4">
        <SelectButton
          v-model="form.is_admin"
          :options="roleOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          :aria-label="t('role')" />
        <small class="text-surface-500">{{ t('admin_hint') }}</small>
      </div>
    </div>
    <div class="flex justify-end gap-2">
      <Button type="button" :label="t('cancel')" severity="secondary" @click="isVisible = false" />
      <Button type="button" :label="t('ok')" :loading="form.processing" @click="createUser" />
    </div>
  </Dialog>
</template>

<i18n>
{
    "en": {
        "title": "Create New User",
        "first_name": "First Name",
        "last_name": "Last Name",
        "email": "Email",
        "role": "Role",
        "roles": {
            "banker": "Banker",
            "admin": "Admin"
        },
        "admin_hint": "Admins can manage users of this bank.",
        "cancel": "Cancel",
        "ok": "Create"
    },
    "de": {
        "title": "Neuen Nutzer anlegen",
        "first_name": "Vorname",
        "last_name": "Nachname",
        "email": "E-Mail",
        "role": "Rolle",
        "roles": {
            "banker": "Banker",
            "admin": "Admin"
        },
        "admin_hint": "Admins können Nutzer dieser Bank verwalten.",
        "cancel": "Abbrechen",
        "ok": "Anlegen"
    }
}
</i18n>
